import type { TamsClient } from '@mimir/common/types/tams';
import type { TamsSegment } from '@mimir/common/tams/schemas';
import type { CmafSegmentSource, CmafSegmentIterator, CmafSegment } from './mseBuffering';
import { inspect } from '@mimir/common/mpegts/inspect';
import { CodecString } from '@mimir/common/dto/mediaAvailability';
import { addFuzzy, humanReadableRange, maxFuzzy, subtractFuzzy } from '@mimir/common/util/fuzzyMath';
import type { Logger } from '@mimir/common/types/logger';
import { findTamsFlowRange, parseTamsTimerange, selectTamsSegmentUrl, serializeTamsPointQuery, serializeTamsTimerange, SEGMENT_BOUNDARY_EPSILON } from '@mimir/common/tams/utils';
import type { FuzzyBounds, FuzzyValue } from '@mimir/common/util/fuzzyNumbers';
import type { BasicFetch } from '@mimir/common/types/basicFetch';
import type { AsyncTransmuxer, AsyncTransmuxerProvider } from './asyncTransmuxer';
import { readWholeStream } from '@mimir/common/util/readableStream';
import { isAbortError } from '@mimir/common/util/abortControl';
import { Lock } from '@mimir/common/async/lock';
import { fetchOrThrow } from '@mimir/common/io/fetchOrThrow';
import { asyncTimeout } from '@mimir/common/async/asyncTimeout';
import { PrefixedLogger } from '@mimir/common/log/prefixedLogger';

export function createTamsCmafSegmentSource(
  services: {
    asyncTransmuxerProvider: AsyncTransmuxerProvider,
    fetch: BasicFetch,
    logger: Logger,
    tamsClient: TamsClient
  },
  abortSignal: AbortSignal,
  playerDebugId: string,
  flowId: string,
): CmafSegmentSource {
  const logger = new PrefixedLogger(services.logger, `[${playerDebugId}:tams:${flowId}]`);
  return new TamsCmafSegmentSource({ ...services, logger }, abortSignal, playerDebugId, flowId);
}

// End of public api

class TamsCmafSegmentSource implements CmafSegmentSource {
  private flowInfo : TamsFlowInfo | null = null;
  private cachedFlowInfoLock : Lock = new Lock();

  private firstSegment : Uint8Array | null = null;
  private firstSegmentLock : Lock = new Lock();
  private iteratorCounter : number = 0;

  public constructor(
    private services: {
      asyncTransmuxerProvider: AsyncTransmuxerProvider,
      fetch: BasicFetch,
      logger: Logger,
      tamsClient: TamsClient
    },
    private abortSignal: AbortSignal,
    private playerDebugId: string,
    private flowId: string,
  ) {

  }

  public humanReadableDescription(): string {
    return `tams:${this.flowId}`;
  }

  public async codec(): Promise<string> {
    // Same policy as the proven live-TS player path
    // (mimirStreamRoleCmafSegmentSource): inspect the first segment only for
    // which essences are present and answer with the same fixed codec strings.
    // The TAMS flow's single-essence `codec` field cannot express the muxed
    // video+audio TS Mimir records live, and the transmuxer downmixes all
    // source audio into one FLAC track, so audio is always declared as flac.
    // For a video-only h264 flow this yields the exact string the pre-TAMS-live
    // code produced.
    const info = inspect(await this.getCachedFirstSegment(), true);
    const hasAudio = info?.hasAudio ?? false;
    const hasVideo = info?.hasVideo ?? false;
    if (hasAudio) {
      return hasVideo ? CodecString.MP4_VIDEO_WITH_FLAC : CodecString.MP4_AUDIO_FLAC;
    }
    if (hasVideo) {
      return CodecString.MP4_VIDEO_H264_BASELINE_AVC_20;
    }
    throw new Error(`TAMS flow ${this.flowId}: first segment has neither audio nor video`);
  }

  public async createIterator(
    startTime: FuzzyValue | null,
    _invalidateBuffer: () => void,
  ): Promise<CmafSegmentIterator> {
    const firstSegment = await this.getCachedFirstSegment();
    const flowInfo = await this.getCachedTamsFlowInfo();

    const requestedStartTime =
      maxFuzzy(startTime ?? flowInfo.range.start, flowInfo.range.start);

    let iteratorStartTime = requestedStartTime;

    // The CMAF iterator need to report its start position meaning that we need
    // to query the actual segment range at the requested start time.

    const timerange = serializeTamsPointQuery(requestedStartTime);
    const [tamsSegment] = await this.services.tamsClient.getSegments({ flowId: this.flowId, timerange });
    if (tamsSegment !== undefined) {
      iteratorStartTime = parseTamsTimerange(tamsSegment.timerange).start;
    }

    const logger = new PrefixedLogger(this.services.logger, `[${this.iteratorCounter}]`);
    this.iteratorCounter += 1;

    // TODO: Potential optimization here is to pass the TamsSegment in there
    // so that the iterator does not have to re-request this TamsSegment on
    // the first next() call. This may speed up seeking with tams.
    return new TamsCmafSegmentIterator(
      {
        ...this.services,
        logger,
      },
      this.abortSignal,
      this.playerDebugId,
      this.flowId,
      iteratorStartTime,
      firstSegment,
    );
  }

  private async getCachedFirstSegment(): Promise<Uint8Array> {
    try {
      await this.firstSegmentLock.waitForLock();
      if (this.firstSegment === null) {
        const { tamsClient } = this.services;
        const [segment] = await tamsClient.getSegments({ flowId: this.flowId, limit: 1 });
        if (segment === undefined) {
          throw new Error('Unable to obtain first segment from TAMS');
        }
        this.firstSegment = await fetchTamsSegment(this.services, segment);
      }
      return this.firstSegment;
    } finally {
      this.firstSegmentLock.releaseLock();
    }
  }

  public async duration(): Promise<FuzzyValue> {
    const flowInfo = await this.getCachedTamsFlowInfo();
    return subtractFuzzy(flowInfo.range.end, flowInfo.range.start);
  }

  // end of public API

  private async getCachedTamsFlowInfo(): Promise<TamsFlowInfo> {
    try {
      await this.cachedFlowInfoLock.waitForLock();
      if (this.flowInfo === null) {
        this.flowInfo = await this.getTamsFlowInfo();
      }
      return this.flowInfo;
    } finally {
      this.cachedFlowInfoLock.releaseLock();
    }
  }

  private async getTamsFlowInfo(): Promise<TamsFlowInfo> {
    const { logger, tamsClient } = this.services;
    const range = await findTamsFlowRange(logger, tamsClient, this.flowId, this.flowInfo?.range ?? null);
    return { range };
  }
}

interface TamsFlowInfo {
  range: FuzzyBounds,
}


class TamsCmafSegmentIterator implements CmafSegmentIterator {
  private currentPosition : FuzzyValue;
  private transmuxer : AsyncTransmuxer;
  private abortController : AbortController = new AbortController();
  private isInEofState : boolean = false;
  // True until the first real (non-init) segment has been fetched. Only from
  // then on does `currentPosition` actually sit on a boundary shared with a
  // previous segment — see the comment in `nextInternal`.
  private hasFetchedFirstRealSegment : boolean = false;

  constructor(
    private services: {
      asyncTransmuxerProvider: AsyncTransmuxerProvider
      fetch: BasicFetch,
      logger: Logger,
      tamsClient: TamsClient,
    },
    abortSignal: AbortSignal,
    private playerDebugId: string,
    private flowId: string,
    private _startPosition: FuzzyValue,
    private firstSegment: Uint8Array | null,
  ) {
    abortSignal.addEventListener('abort', () => this.abortController.abort());
    this.transmuxer = services.asyncTransmuxerProvider(abortSignal);
    this.currentPosition = _startPosition;
  }

  async next(bufferedRanges: FuzzyBounds[]): Promise<CmafSegment | 'closed'> {
    try {
      while (true) {
        const result = await this.nextInternal(bufferedRanges);
        if (result === 'retry') {
          await asyncTimeout(1000);
          continue;
        }
        if (result === 'skip') {
          continue;
        }
        return result;
      }
    } catch (e) {
      if (isAbortError(e)) {
        return 'closed';
      }
      throw e;
    }
  }

  async nextInternal(_bufferedRanges: FuzzyBounds[]): Promise<CmafSegment | 'retry' | 'skip'> {
    const { logger } = this.services;
    // TODO: Use bufferedRanges to potentially skip segments.
    const signal = this.abortController.signal;

    signal.throwIfAborted();

    if (this.firstSegment) {
      const output =
        await this.transmuxer(
          this.playerDebugId,
          this.firstSegment,
          // Empty mix matrix => the transmuxer builds a default downmix and
          // emits FLAC audio (a null matrix disables audio and yields no
          // fragment for Mimir's multi-stream live TS). Matches the non-TAMS
          // live source; for the init segment the matrix contents are moot.
          [],
          true,
        );

      signal.throwIfAborted();

      this.firstSegment = null;

      logger.info('TAMS iterator returning initialization segment');

      return {
        bytes: output.initialization,
        timeRange: null,
      };
    }

    // On the very first real segment, `currentPosition` is the iterator's
    // start position resolved by `createIterator` (itself already a
    // `serializeTamsPointQuery` lookup) — there's no previous segment sharing
    // that boundary, so a genuine tolerant range is safe and correct here,
    // same as any other `serializeTamsPointQuery` caller.
    //
    // From the second real segment on, `currentPosition` is always the "end"
    // value of the previous segment, shared with the start of the next one.
    // Unlike serializeTamsPointQuery's other callers, this position sits
    // exactly ON a real boundary between two adjacent segments, so a plain
    // [position, position + epsilon) range risks ambiguously overlapping the
    // tail of the *previous* segment too. Nudge forward past the boundary
    // first, into the middle of the intended next segment, then probe a
    // zero-width point there — safely clear of both neighbors, so no
    // range-based tolerance is needed on top.
    const probePosition = this.hasFetchedFirstRealSegment
      ? addFuzzy(this.currentPosition, SEGMENT_BOUNDARY_EPSILON)
      : this.currentPosition;

    logger.info(`TAMS iterator looking for segment at position: ${probePosition.approximate.toFixed(2)}`);
    const timerange = this.hasFetchedFirstRealSegment
      ? serializeTamsTimerange({ start: probePosition, end: probePosition }, 'inclusive', 'inclusive')
      : serializeTamsPointQuery(probePosition);

    const { tamsClient } = this.services;
    const flowId = this.flowId;

    const [tamsSegment] =
      await tamsClient.getSegments({ flowId, timerange, limit: 1 });

    if (tamsSegment === undefined) {
      if (!this.isInEofState) {
        logger.info(`Reached EOF at ${probePosition.approximate.toFixed(2)}. Will retrying in case the stream grows`);
        this.isInEofState = true;
      }
      return 'retry';
    }

    this.hasFetchedFirstRealSegment = true;

    if (this.isInEofState) {
      logger.info(`TAMS iterator has growing at position ${probePosition.approximate.toFixed(2)}. Will resume providing segments.`);
      this.isInEofState = false;
    }

    const timeRange = parseTamsTimerange(tamsSegment.timerange);

    logger.info(`TAMS iterator got segment with range: ${humanReadableRange(timeRange)}`);

    const tsBytes = await fetchTamsSegment(this.services, tamsSegment);
    const { mediaSegment: bytes } = await this.transmuxer(this.playerDebugId, tsBytes, [], false);

    const cmafSegment : CmafSegment = { bytes, timeRange, };

    const segmentRange = parseTamsTimerange(tamsSegment.timerange);
    this.currentPosition = segmentRange.end;

    logger.info(`TAMS iterator returning segment and incrementing position to ${this.currentPosition.approximate.toFixed(0)}`);

    return cmafSegment;
  }

  startPosition(): FuzzyValue {
    return this._startPosition;
  }

  close(): void {
    this.abortController.abort();
  }
}

async function fetchTamsSegment(
  services: {
    fetch: BasicFetch,
    logger: Logger,
  },
  segment: TamsSegment
): Promise<Uint8Array> {
  const { fetch, logger } = services;

  const url = selectTamsSegmentUrl(segment);
  const response = await fetchOrThrow(logger, fetch, url, { });
  if (!response.ok) {
    throw new Error(`Unable to fetch TAMS media segment from ${url}`);
  }
  if (response.body === null) {
    throw new Error('response body for media segment is empty');
  }
  return await readWholeStream(response.body);
}
